import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Clock, CornerDownLeft, Search } from "lucide-react";
import Kbd from "../components/Kbd";
import { useAdmin } from "../lib/useAdmin";

/* Palette ⌘K. Elle ne va rien chercher elle-même : les sources de recherche et
   les actions lui sont passées en props, ce qui la rend testable et lui évite
   de connaître l'API. Chaque source expose search(q) et peut être asynchrone.

   searchSources: [{ id, label, icon, perm?, search(query) -> [{ id, title, subtitle, to?, run?, badge? }] }]
   actions:       [{ id, label, group?, icon?, shortcut?, perm?, keywords?, run() }] */

const RECENTS = "perform.admin.palette-recents";
const MAX_RECENTS = 6;

function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry) {
  try {
    const next = [entry, ...readRecents().filter(r => r.id !== entry.id)].slice(0, MAX_RECENTS);
    window.localStorage.setItem(RECENTS, JSON.stringify(next));
    return next;
  } catch {
    return readRecents();
  }
}

export default function CommandPalette({
  open,
  onClose,
  searchSources = [],
  actions = [],
  onNavigate,
  placeholder = "Rechercher un utilisateur, une organisation, un événement d'audit… ou lancer une action",
}) {
  const { can } = useAdmin();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(readRecents);
  const listRef = useRef(null);
  const run = useRef(0);
  // Sources et permissions passent par des refs : elles changent d'identité à
  // chaque rendu du parent et relanceraient la recherche en boucle.
  const sourcesRef = useRef(searchSources);
  sourcesRef.current = searchSources;
  const canRef = useRef(can);
  canRef.current = can;

  useEffect(() => {
    if (open) { setQuery(""); setDebounced(""); setResults([]); setActive(0); setRecents(readRecents()); }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 140);
    return () => clearTimeout(timer);
  }, [query]);

  // Les sources sont interrogées en parallèle ; un compteur écarte les
  // réponses d'une frappe déjà dépassée.
  useEffect(() => {
    if (!open || !debounced) { setResults([]); setSearching(false); return undefined; }
    const ticket = run.current + 1;
    run.current = ticket;
    setSearching(true);
    let cancelled = false;

    Promise.all(sourcesRef.current
      .filter(source => canRef.current(source.perm))
      .map(source => Promise.resolve()
        .then(() => source.search(debounced))
        .then(items => ({ source, items: items || [] }))
        .catch(() => ({ source, items: [] }))))
      .then(groups => {
        if (cancelled || run.current !== ticket) return;
        setResults(groups.filter(g => g.items.length));
        setSearching(false);
        setActive(0);
      });

    return () => { cancelled = true; };
  }, [open, debounced]);

  const visibleActions = useMemo(() => {
    const allowed = actions.filter(action => can(action.perm));
    if (!debounced) return allowed;
    const needle = debounced.toLowerCase();
    return allowed.filter(action => `${action.label} ${action.keywords || ""}`.toLowerCase().includes(needle));
  }, [actions, debounced, can]);

  // Liste plate pour la navigation clavier, groupes conservés pour l'affichage.
  const groups = useMemo(() => {
    const out = [];
    if (!debounced && recents.length) {
      out.push({ id: "recents", label: "Récents", icon: Clock, items: recents });
    }
    // Les actions sont regroupées par leur champ `group` (« Actions », « Aller à »…).
    const byGroup = new Map();
    visibleActions.forEach(action => {
      const label = action.group || "Actions";
      if (!byGroup.has(label)) byGroup.set(label, []);
      byGroup.get(label).push({ ...action, title: action.label });
    });
    byGroup.forEach((items, label) => out.push({ id: `actions:${label}`, label, items }));
    results.forEach(({ source, items }) => out.push({ id: source.id, label: source.label, icon: source.icon, items }));
    return out;
  }, [debounced, recents, visibleActions, results]);

  const flat = useMemo(() => groups.flatMap(g => g.items.map(item => ({ ...item, groupId: g.id }))), [groups]);

  const select = useCallback(item => {
    if (!item) return;
    setRecents(pushRecent({
      id: `${item.groupId}:${item.id}`,
      title: item.title,
      subtitle: item.subtitle,
      to: item.to,
      groupId: item.groupId,
    }));
    onClose?.();
    if (item.run) item.run();
    else if (item.to) onNavigate?.(item.to, item);
  }, [onClose, onNavigate]);

  useEffect(() => {
    const node = listRef.current?.querySelector('[data-active="true"]');
    node?.scrollIntoView({ block: "nearest" });
  }, [active, groups]);

  if (!open) return null;

  const onKeyDown = event => {
    if (event.key === "Escape") { event.stopPropagation(); event.preventDefault(); onClose?.(); return; }
    if (event.key === "ArrowDown") { event.preventDefault(); setActive(i => (flat.length ? (i + 1) % flat.length : 0)); return; }
    if (event.key === "ArrowUp") { event.preventDefault(); setActive(i => (flat.length ? (i - 1 + flat.length) % flat.length : 0)); return; }
    if (event.key === "Enter") { event.preventDefault(); select(flat[active]); }
  };

  let cursor = -1;

  return (
    <div
      className="adm-palette-backdrop"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}
      onKeyDown={onKeyDown}
    >
      <div className="adm-palette" role="dialog" aria-modal="true" aria-label="Palette de commandes" data-testid="command-palette">
        <div className="adm-palette-input">
          <Search size={16} color="var(--adm-ink-3)" />
          {/* Le focus initial dans le champ est le comportement attendu d'une palette. */}
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label="Rechercher ou lancer une action"
            aria-controls="adm-palette-list"
            data-testid="palette-input"
          />
          {searching && <span className="adm-spinner" aria-label="Recherche en cours" />}
        </div>

        <div className="adm-palette-list" id="adm-palette-list" ref={listRef}>
          {!flat.length && (
            <p className="adm-palette-empty" data-testid="palette-empty">
              {debounced
                ? `Aucun résultat pour « ${debounced} ». Les résultats se limitent à ce que vos permissions autorisent.`
                : "Tapez pour chercher un utilisateur, une organisation ou un événement d'audit."}
            </p>
          )}

          {groups.map(group => (
            <div key={group.id}>
              <div className="adm-palette-group">{group.label}</div>
              {group.items.map(item => {
                cursor += 1;
                const index = cursor;
                const Icon = item.icon || group.icon || ArrowRight;
                return (
                  <button
                    type="button"
                    key={`${group.id}-${item.id}`}
                    className={`adm-palette-item${index === active ? " is-active" : ""}`}
                    data-active={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => select({ ...item, groupId: group.id })}
                    data-testid={`palette-item-${item.id}`}
                  >
                    <Icon size={15} />
                    <div>
                      <strong>{item.title}</strong>
                      {item.subtitle && <small>{item.subtitle}</small>}
                    </div>
                    {item.badge && <span className="adm-badge">{item.badge}</span>}
                    {item.shortcut && <Kbd combo={item.shortcut} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="adm-palette-foot">
          <span><Kbd keys={["up", "down"]} /> naviguer</span>
          <span><CornerDownLeft size={11} /> ouvrir</span>
          <span><Kbd combo="esc" /> fermer</span>
          <span style={{ marginLeft: "auto" }}>Résultats limités à vos permissions</span>
        </div>
      </div>
    </div>
  );
}
